/**
 * "Show the intro?" state machine, per competition (specs/competitions-hub-client Slice 3D).
 *
 * Mirrors the World Cup welcome gate in useWorldCupLayout, with one deliberate fork:
 * the WC backfills established players (≥1 challenge) so they never see its intro,
 * which is right for a first-run tutorial but wrong for a season pool — an existing
 * Zporti player still needs the season rules explained. Callers opt in explicitly.
 */

import { useCallback, useEffect, useState } from 'react';
import { useHasExistingChallenges } from '../../challenges/hooks/useHasExistingChallenges';
import { useWorldCupWelcomeMenuStore } from '../../worldcup/stores/worldCupWelcomeMenuStore';
import {
  readCompetitionIntroCompleted,
  writeCompetitionIntroCompleted,
} from '../helpers/competitionIntroStorage.helper';

export type CompetitionIntroUiState = 'loading' | 'show' | 'hidden';

interface UseCompetitionIntroArgs {
  competitionId: string;
  isLoaded: boolean;
  isSignedIn: boolean | null;
  userId: string | null;
  /** World Cup only: treat an established player as having completed the intro. */
  applyExistingPlayerBackfill?: boolean;
  /** False disables the gate entirely (e.g. competition has no intro content). */
  enabled?: boolean;
  /**
   * Content discriminator from `CompetitionIntroConfig`. Part of the storage key, so correcting
   * the rules copy re-shows the intro once instead of being invisible to everyone who already
   * dismissed the old version (specs/champions-league Story 8).
   */
  contentId?: string | null;
}

export function useCompetitionIntro({
  competitionId,
  isLoaded,
  isSignedIn,
  userId,
  applyExistingPlayerBackfill = false,
  enabled = true,
  contentId = null,
}: UseCompetitionIntroArgs): {
  introUi: CompetitionIntroUiState;
  handleIntroFinished: () => Promise<void>;
  showIntro: () => void;
} {
  const [introUi, setIntroUi] = useState<CompetitionIntroUiState>('loading');
  const { isResolved: challengesResolved, hasChallenges } = useHasExistingChallenges();
  const setWelcomeOverlayVisible = useWorldCupWelcomeMenuStore((s) => s.setWelcomeOverlayVisible);

  /**
   * Same flag the World Cup welcome raises (useWorldCupLayout): it drives
   * `shouldHideTabBar` in `app/app/_layout.tsx` and the floating create / Warmup buttons.
   * The intro is a full-screen takeover mounted inside the `/app` Tabs group, so without
   * this the tab bar keeps occupying the bottom of the screen and competes with the intro's
   * own bottom-anchored CTA. Hiding it is what the World Cup does; it also removes any
   * question of the two stacking against each other.
   */
  useEffect(() => {
    setWelcomeOverlayVisible(introUi === 'show');
    return () => {
      setWelcomeOverlayVisible(false);
    };
  }, [setWelcomeOverlayVisible, introUi]);

  useEffect(() => {
    if (!enabled || !competitionId) {
      setIntroUi('hidden');
      return;
    }
    // Wait for identity before choosing a storage key — reading the 'anonymous' key
    // for a signed-in user would flash the intro and then re-hide it.
    if (!isLoaded) return;
    if (isSignedIn && !userId) return;
    if (applyExistingPlayerBackfill && isSignedIn && !challengesResolved) return;

    let cancelled = false;
    void (async () => {
      const done = await readCompetitionIntroCompleted(competitionId, userId, contentId);
      if (cancelled) return;
      if (!done && applyExistingPlayerBackfill && isSignedIn && hasChallenges) {
        void writeCompetitionIntroCompleted(competitionId, userId, contentId);
        setIntroUi('hidden');
        return;
      }
      setIntroUi(done ? 'hidden' : 'show');
    })();
    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    competitionId,
    isLoaded,
    isSignedIn,
    userId,
    applyExistingPlayerBackfill,
    challengesResolved,
    hasChallenges,
    contentId,
  ]);

  const handleIntroFinished = useCallback(async () => {
    setIntroUi('hidden');
    await writeCompetitionIntroCompleted(competitionId, userId, contentId);
  }, [competitionId, userId, contentId]);

  /** Re-open from the header Info button without clearing the stored flag. */
  const showIntro = useCallback(() => setIntroUi('show'), []);

  return { introUi, handleIntroFinished, showIntro };
}
